import { createSelector } from 'reselect'

import {
	TASK_CATEGORIES,
	TASK_TYPES,
	TASK_TYPES_V2,
	TaskPriorityConfig,
	TaskStatusUnion,
	TaskWorkflowProcessesConfig,
	TaskWorkflowStagesConfig,
} from '@app/types/task'
import {
	CalendarEventCategoryConfig,
	CalendarEventCategoryConfigV2,
	CalendarEventStatusConfig,
	CalendarEventTypeConfig,
	EVENT_CATEGORY_COLORS,
	EVENT_CATEGORY_VALUE,
	EVENT_TYPES,
} from '@app/types/calendar'
import {
	SystemConfigAccountRef,
	SystemConfigAgeGroups,
	SystemConfigCarbs,
	SystemConfigCountryData,
	SystemConfigExpertise,
	SystemConfigGenders,
	SystemConfigLanguage,
	SystemConfigOrganizationStatus,
	SystemConfigOrganizationType,
	SystemConfigReminder,
	SystemConfigRole,
	SystemConfigRoleType,
	SystemConfigSkills,
	SystemConfigSmsLanguage,
	SystemConfigSmsTemplate,
	SystemConfigTemplatesItem,
	SystemConfigUIName,
	SystemConfigUIText,
	SystemConfigUserTitle,
	TimeZones,
	SystemConfigValueParam,
	SystemTaskStatusesV2,
} from '@app/types/systemConfig'
import { RootState } from '@app/store'
import { ReportPeriodFormData, ReportTypeFormData } from '@app/types/reports'
import { COMORBIDITIES, DIABETES_TYPES, PARTS_OF_DAY } from '@app/types/common'

export const selectTaskStatuses = (
	state: RootState
): Array<SystemConfigValueParam<string, TaskStatusUnion>> =>
	state.systemConfig.system?.taskStatuses || []

export const selectTaskPriorities = (
	state: RootState
): Array<TaskPriorityConfig> => state.systemConfig.system?.taskPriorities || []

export const selectV1TaskTypes = (
	state: RootState
): Array<SystemConfigValueParam<string, TASK_TYPES>> =>
	state.systemConfig.system?.taskTypes || []

export const selectTaskTypes = (): Array<
	SystemConfigValueParam<string, TASK_TYPES_V2>
> => {
	return [
		{
			id: '1',
			value: TASK_TYPES_V2.INTERNAL_TASK,
		},
		{
			id: '2',
			value: TASK_TYPES_V2.OUTBOUND_CALL,
		},
		{
			id: '3',
			value: TASK_TYPES_V2.INBOUND_CALL,
		},
		{
			id: '4',
			value: TASK_TYPES_V2.CHAT,
		},
		{
			id: '5',
			value: TASK_TYPES_V2.PERSONAL,
		},
	]
}

export const selectV1V2TaskTypes = createSelector(
	[selectV1TaskTypes, selectTaskTypes],
	(v1TaskTypes, v2TaskTypes) => [...v1TaskTypes, ...v2TaskTypes]
)

export const selectTaskReminders = (
	state: RootState
): Array<SystemConfigReminder> => {
	const reminders = state.systemConfig?.system?.taskReminders || []

	return reminders.map(reminder => {
		if (reminder.display.match(/\d/g)) {
			return {
				...reminder,
				display: `${reminder.display} before`,
			}
		}

		return reminder
	})
}

export const selectWorkflowProcesses = (
	state: RootState
): Array<TaskWorkflowProcessesConfig> =>
	state.systemConfig.system?.workflowProcesses || []

export const selectWorkflowStages = (
	state: RootState
): Array<TaskWorkflowStagesConfig> =>
	state.systemConfig.system?.workflowStages || []

export const selectEventTypes = (
	state: RootState
): Array<CalendarEventTypeConfig> => {
	const usingEventTypes = [
		EVENT_TYPES.CALL,
		EVENT_TYPES.VIDEO,
		EVENT_TYPES.GROUP_SESSION,
		EVENT_TYPES.WEBINAR,
	]

	const eventTypes = state.systemConfig.system?.eventTypes || []

	return eventTypes.filter(eventType =>
		usingEventTypes.includes(+eventType.id)
	)
}

export const selectRoles = (state: RootState): Array<SystemConfigRole> =>
	state.systemConfig.system?.roles || []

export const selectRoleTypes = (
	state: RootState
): Array<SystemConfigRoleType> => state.systemConfig.system?.roleTypes || []

export const selectEventCategoryList = (
	state: RootState
): Array<CalendarEventCategoryConfig> =>
	state.systemConfig.system?.eventCategories || []

export const selectEventCategoryV2List = (
	state: RootState
): Array<CalendarEventCategoryConfigV2> =>
	state.systemConfig.configuration?.eventCategoriesV2 || []

export const selectEventCategories = createSelector(
	[selectEventCategoryList, selectEventCategoryV2List],
	(categoryList): Array<CalendarEventCategoryConfig & { text: string }> => {
		const updatedCategories = categoryList.map(
			(category: CalendarEventCategoryConfig) => ({
				...category,
				text: category.value,
			})
		)

		return [
			{
				id: '0',
				value: EVENT_CATEGORY_VALUE.CATEGORY_0,
				color: EVENT_CATEGORY_COLORS.CATEGORY_0,
				text: 'No category',
				fontColor: '',
			},
			...updatedCategories,
		]
	}
)

export const selectEventStatus = (
	state: RootState
): Array<CalendarEventStatusConfig> =>
	state.systemConfig.system?.eventStatuses || []

export const selectEventReminders = (
	state: RootState
): Array<SystemConfigReminder> => {
	const reminders = state.systemConfig.system?.eventReminders || []

	return reminders.map(reminder => {
		if (reminder.display.match(/\d/g)) {
			return {
				...reminder,
				display: `${reminder.display} before`,
			}
		}

		return reminder
	})
}

export const selectMaritalStatuses = (
	state: RootState
): Array<SystemConfigUIText> => state.systemConfig.system?.martialStatus || []

export const selectCountries = (
	state: RootState
): Array<SystemConfigCountryData> => state.systemConfig.system?.countries || []

export const selectGenders = (state: RootState): Array<SystemConfigGenders> =>
	state.systemConfig.system?.gender || []

export const selectChecklistTemplates = (
	state: RootState
): Array<SystemConfigTemplatesItem> =>
	state.systemConfig.templates?.checklist || []

export const selectChecklistTemplateNames = (
	state: RootState
): Array<string> => {
	const checklistTemplates = state.systemConfig.templates?.checklist || []

	return checklistTemplates.map(item => item.name)
}

export const selectAccountsRef = (
	state: RootState
): Array<SystemConfigAccountRef> =>
	state.systemConfig?.configuration?.accountsRef || []

export const selectAllSystemConfig = (state: RootState): any => {
	// eslint-disable-next-line max-len
	// todo: need check on be side. For now temporary. May not working logic on be side. Need add these info to system config on be side
	return {
		...state.systemConfig.system,
		preferred_contact_time: [
			{
				id: PARTS_OF_DAY.MORNING,
				value: PARTS_OF_DAY.MORNING,
				name: PARTS_OF_DAY.MORNING,
			},
			{
				id: PARTS_OF_DAY.NOON,
				value: PARTS_OF_DAY.NOON,
				name: PARTS_OF_DAY.NOON,
			},
			{
				id: PARTS_OF_DAY.AFTERNOON,
				value: PARTS_OF_DAY.AFTERNOON,
				name: PARTS_OF_DAY.AFTERNOON,
			},
			{
				id: PARTS_OF_DAY.EVENING,
				value: PARTS_OF_DAY.EVENING,
				name: PARTS_OF_DAY.EVENING,
			},
		],
	}
}

export const selectSystemConfigSmsTemplate = (
	state: RootState
): Array<SystemConfigSmsTemplate> =>
	state.systemConfig?.configuration?.sms?.templates || []

export const selectSystemConfigSmsLanguage = (
	state: RootState
): Array<SystemConfigSmsLanguage> =>
	state.systemConfig?.configuration?.sms?.languages || []

export const selectSystemConfigExpertises = (
	state: RootState
): Array<SystemConfigExpertise> =>
	state.systemConfig?.configuration?.expertises || []

export const selectSystemConfigUserTitles = (
	state: RootState
): Array<SystemConfigUserTitle> =>
	state.systemConfig?.configuration?.userTitle || []

export const selectSystemConfigRoles = (
	state: RootState
): Array<SystemConfigRole> => state.systemConfig?.system?.roles || []

export const selectSystemConfigRoleTypes = (
	state: RootState
): Array<SystemConfigRoleType> => state.systemConfig?.system?.roleTypes || []

export const selectSystemConfigLanguages = (
	state: RootState
): Array<SystemConfigLanguage> =>
	state.systemConfig?.system?.languageRegion || []

export const selectReportTypesDropdown = (
	state: RootState
): Array<ReportTypeFormData> => {
	return state.systemConfig.dropDowns.report_type.map(item => {
		return {
			value: item.value,
			label: state.locale.langData[item.ui_name] || '',
			options: item.options,
		}
	})
}
export const selectLangData = (state: RootState): any =>
	state.locale.langData || {}

export const selectReportPeriodsDropdown = (
	state: RootState
): Array<ReportPeriodFormData> => {
	return state.systemConfig.dropDowns.reports_period.map(item => {
		return {
			start: item.start_time,
			end: item.end_time,
			label: state.locale?.langData?.[item.ui_name] || '',
			value: state.locale?.langData?.[item.ui_name] || '',
		}
	})
}

export const selectReportYearsDropdown = (
	state: RootState
): Array<{ label: string; value: string }> => {
	const options = state.systemConfig.dropDowns.report_type.find(
		type => !!type.options?.length
	)?.options

	const years = options?.find(opt => opt.name === 'year')?.values

	return (
		years?.map(opt => ({
			label: opt,
			value: opt,
		})) || []
	)
}

export const selectReportMonthsDropdown = (
	state: RootState
): Array<{ label: string; value: string }> => {
	const options = state.systemConfig.dropDowns.report_type.find(
		type => !!type.options?.length
	)?.options

	const months = options?.find(opt => opt.name === 'month')?.values

	return (
		months?.map(opt => ({
			label: opt,
			value: opt,
		})) || []
	)
}

export const selectSkillsList = (state: RootState): Array<SystemConfigSkills> =>
	state.systemConfig.configuration?.skills || []

export const selectExpertises = (
	state: RootState
): Array<SystemConfigExpertise> =>
	state.systemConfig.configuration?.expertises || []

export const selectOrganizationTypes = (
	state: RootState
): Array<SystemConfigOrganizationType> => {
	return (
		(state.systemConfig.system
			.organizationType as Array<SystemConfigOrganizationType>) || []
	)
}
export const selectOrganizationStatus = (
	state: RootState
): Array<SystemConfigOrganizationStatus> => {
	return (
		(state.systemConfig.system
			.organizationStatus as Array<SystemConfigOrganizationStatus>) || []
	)
}

export const selectTimeZones = (state: RootState): Array<TimeZones> => {
	return state.systemConfig?.system?.timezones
}

export const selectDiabetesTypes = (
	state: RootState
): Array<SystemConfigUIName<DIABETES_TYPES, keyof typeof DIABETES_TYPES>> =>
	state.systemConfig?.system?.diabetesType || []

export const selectInsulinTreatmentConfig = (
	state: RootState
): Array<SystemConfigUIName> =>
	state.systemConfig?.system?.insulinTreatment || []

export const selectSmokingStatuses = (
	state: RootState
): Array<SystemConfigUIText> => state.systemConfig?.system?.smoking || []

export const selectComorbidiesOpts = (
	state: RootState
): Array<SystemConfigValueParam<COMORBIDITIES, COMORBIDITIES>> => {
	const comorbidities = state.systemConfig?.system?.comorbiditiesOpts || []

	return [
		...comorbidities,
		{
			id: COMORBIDITIES.SLEEP_DISORDER,
			value: COMORBIDITIES.SLEEP_DISORDER,
		},
	]
}

export const selectComorbitiesOptsValues = (
	state: RootState
): Array<string> => [
	...(state.systemConfig?.system?.comorbiditiesOpts || []).map(
		item => item.value
	),
	COMORBIDITIES.SLEEP_DISORDER,
] // todo: 'Sleep disorder should be added to the system config options

export const selectMembershipProducts = (
	state: RootState
): Array<SystemConfigUIText> => {
	return (
		state.systemConfig?.userConfig?.myOrganization?.membershipEnrollment
			?.products || []
	)
}

export const selectSystemConfigCarbs = (
	state: RootState
): Array<SystemConfigCarbs> => state.systemConfig?.system?.carbs || []

export const selectAgesGroups = (
	state: RootState
): Array<SystemConfigAgeGroups> => state.systemConfig?.system?.age || []

export const selectSystemConfigTaskCategories = (
	state: RootState
): Array<SystemConfigValueParam<number, TASK_CATEGORIES>> =>
	state.systemConfig?.system?.taskCategories || []

export const selectSystemConfigTaskStatusesV2 = (
	state: RootState
): SystemTaskStatusesV2 => state.systemConfig?.system?.taskStatusesV2
